import { get_axios_letter_instance, api_letter_management, errorHandler } from '@/apis/variables.js';

// Create axios instances for different endpoints
const templates_instance = get_axios_letter_instance(api_letter_management.SERVICE_LETTER_TEMPLATES);
const template_details_instance = get_axios_letter_instance(api_letter_management.SERVICE_TEMPLATE_DETAILS);
const csv_template_instance = get_axios_letter_instance(api_letter_management.SERVICE_CSV_TEMPLATE);
const upload_csv_instance = get_axios_letter_instance(api_letter_management.SERVICE_UPLOAD_CSV);
const delete_request_instance = get_axios_letter_instance(api_letter_management.SERVICE_DELETE_REQUEST);
const process_letter_instance = get_axios_letter_instance(api_letter_management.SERVICE_PROCESS_LETTER);
const process_selected_instance = get_axios_letter_instance(api_letter_management.SERVICE_PROCESS_SELECTED_LETTERS);
const bulk_download_instance = get_axios_letter_instance(api_letter_management.SERVICE_BULK_DOWNLOAD);
const bulk_download_zip_instance = get_axios_letter_instance(api_letter_management.SERVICE_BULK_DOWNLOAD_ZIP);

const state = {
  templates: [],
  letters: [],
  bulkDownloadResponse: null,
  templateDetails: {},
  csvTemplate: null,
  csvTemplateResponse: null,
  uploadResponse: null,
  isFetching: false,
  isSaving: false,
  isProcessing: false,
  isDownloading: false,
  isLoading: false
};

const mutations = {
  setTemplates: (state, payload) => {
    state.templates = payload;
  },

  setLetters: (state, payload) => {
    state.letters = payload;
  },

  setBulkDownloadResponse: (state, payload) => {
    state.bulkDownloadResponse = payload;
  },

  setTemplateDetails: (state, payload) => {
    state.templateDetails = payload;
  },

  setCsvTemplate: (state, payload) => {
    state.csvTemplate = payload;
  },

  setCsvTemplateResponse: (state, payload) => {
    state.csvTemplateResponse = payload;
  },

  setUploadResponse: (state, payload) => {
    state.uploadResponse = payload;
  },

  setFetching: (state, payload) => {
    state.isFetching = payload;
  },

  setSaving: (state, payload) => {
    state.isSaving = payload;
  },

  setProcessing: (state, payload) => {
    state.isProcessing = payload;
  },

  setDownloading: (state, payload) => {
    state.isDownloading = payload;
  },

  setLoading: (state, payload) => {
    state.isLoading = payload;
  }
};

const actions = {
  // Fetch all letter templates
  async fetchTemplates({ commit }, leId) {
    try {
      commit("setLoading", true);
      let res = await templates_instance.get(`/${leId}`)  
      if (res.status === 200) {
        commit("setTemplates", res.data || []);
      }
      commit("setLoading", false);
     
      return res;
    } catch (err) {
      commit("setLoading", false);
      throw errorHandler(err.response);
    }
  },

  // Get template details
  async getTemplateDetails({ commit }, data) {
    try {
      const { templateId, leId } = data;
      commit("setFetching", true);
      let res = await template_details_instance.get(`/${templateId}/${leId}`);
      if (res.status === 200) {
        commit("setTemplateDetails", res.data);
      }
      commit("setFetching", false);
      return res;
    } catch (err) {
      commit("setFetching", false);
      throw errorHandler(err.response);
    }
  },

  // Get CSV template
  async getCsvTemplate({ commit }, templateId) {
    try {
      commit("setFetching", true);
      let res = await csv_template_instance.get(`/${templateId}`);
      commit("setFetching", false);
      if (res.status === 200) {
        commit("setCsvTemplate", res.data);
        commit("setCsvTemplateResponse", res.data);
      }
      return res;
    } catch (err) {
      commit("setFetching", false);
      throw errorHandler(err.response);
    }
  },

  // Upload CSV file
  async uploadCsv({ commit }, payload) {
    try {
      commit("setSaving", true);
      let res = await upload_csv_instance.post('', payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.status == 201 || res.status == 200) {
        console.log('Success: Setting upload response to state:', res.data);
        commit("setUploadResponse", res.data);
      } 
      commit("setSaving", false);
      return res;
    } catch (err) {
      commit("setSaving", false);
      if (err.response && err.response.status == 400) {
        commit("setUploadResponse", err.response.data);
        return err.response;
      }
      throw errorHandler(err.response);
    }
  },

  // Delete letter request
  async deleteRequest({ commit }, id) {
    try {
      commit("setSaving", true);
      let res = await delete_request_instance.post(`/${id}`);
      commit("setSaving", false);
      return res;
    } catch (err) {
      commit("setSaving", false);
      throw errorHandler(err.response);
    }
  },

  // Process single letter
  async processLetter({ commit }, id) {
    try {
      commit("setProcessing", true);
      let res = await process_letter_instance.post(`/${id}`);
      commit("setProcessing", false);
      return res;
    } catch (err) {
      commit("setProcessing", false);
      throw errorHandler(err.response);
    }
  },

  // Process selected letters
  async processSelectedLetters({ commit }, payload) {
    try {
      commit("setProcessing", true);
      let res = await process_selected_instance.post('', payload);
      commit("setProcessing", false);
      return res;
    } catch (err) {
      commit("setProcessing", false);
      throw errorHandler(err.response);
    }
  },

  // Fetch letters (for the table display)
  async fetchLetters({ commit }, payload) {
    try {
      commit("setFetching", true);
      let res = await bulk_download_instance.get('/', {
        params: payload
      });
      commit("setFetching", false);
      if (res.status === 200) {
        commit("setLetters", res.data.downloadUrls || []);
      }
      return res;
    } catch (err) {
      commit("setFetching", false);
      throw errorHandler(err.response);
    }
  },

  // Fetch letter requests for Generate Letters tab
  async fetchLetterRequests({ commit }, payload) {
    try {
      commit("setFetching", true);
      let res = await upload_csv_instance.get('/', {
        params: payload
      });
      commit("setFetching", false);
      return res;
    } catch (err) {
      commit("setFetching", false);
      throw errorHandler(err.response);
    }
  },

  // Bulk download letters
  async bulkDownload({ commit }, payload) {
    try {
      commit("setFetching", true);
      let res = await bulk_download_instance.post('/', payload);
      console.log('bulkDownload API response:', res.data);
      if (res.status === 201) {
        commit("setBulkDownloadResponse", res.data || []);

      }
      commit("setFetching", false);
      return res;
    } catch (err) {
      commit("setFetching", false);
      throw errorHandler(err.response);
    }
  },

  // Bulk download as ZIP
  async bulkDownloadZip({ commit }, payload) {
    try {
      commit("setDownloading", true);
      let res = await bulk_download_zip_instance.post('/', payload, {
        responseType: 'blob'
      });
      commit("setDownloading", false);
      return res;
    } catch (err) {
      commit("setDownloading", false);
      // console.log(err, 'error in service file inside catch block err')
      // console.log(err.response, 'err response ')
      // const error = {
      //   error: err.response && err.response.data.error 
      // };
      throw err.response.data
    }
  }


};

export default {
  namespaced: true,
  state,
  mutations,
  actions
};